import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import AddSchedule from "../../pages/Schedule/AddSchedule";
import {
  CalendarMonthOutlined,
  CheckCircleOutline,
  KeyboardArrowDown,
  KeyboardArrowUp,
  PendingActionsOutlined,
  PlayCircleOutline,
  ScheduleOutlined,
} from "@mui/icons-material";
import config from "../../config";
import SubTitleNew from "../common/New/SubTitleNew";
import StatsCard from "../common/New/StatsCard";
import DynamicTable from "../common/New/DynamicTable";
import DeleteModal from "../common/DeleteModal";
import { useTranslation } from "react-i18next";
import { usePermission } from "../../hooks/usePermission";
import { useUser } from "../../pages/login/UserContextPersistent";
import layout from "../../config/scheduleDashboardLayout.json";
import ScheduleDashboardSections from "./ScheduleDashboardSections";
import {
  downloadBackendExcel,
  exportTableToCSV,
} from "../../utils/exportTableData";
import { getOrganizationId } from "../manageData/reportsApi";
import "./NewScheduleMain.css";

const ICONS = {
  calendar: CalendarMonthOutlined,
  clock: ScheduleOutlined,
  upcoming: PendingActionsOutlined,
  delay: PlayCircleOutline,
  completed: CheckCircleOutline,
};
const titleCase = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "N/A";
const frequencyLabel = (value) => {
  const key = String(value || "").toUpperCase().replace(/[\s_]+/g, "");
  if (key === "DAILY") return "Daily";
  if (key === "WEEKLY") return "Weekly";
  if (key === "MONTHLY") return "Monthly";
  if (key === "ONETIME") return "One time";
  return titleCase(value);
};
const localeFor = (language) =>
  ({
    en: "en-US",
    fr: "fr-FR",
    de: "de-DE",
    es: "es-ES",
    hi: "hi-IN",
    ar: "ar-SA",
    zh: "zh-CN",
    kn: "kn-IN",
  })[String(language || "en").split("-")[0]] || "en-US";
const formatTime = (value, locale = "en-US") => {
  if (!value) return "N/A";
  if (typeof value === "string") {
    if (value.includes("AM") || value.includes("PM")) return value;
    const match = value.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = match[2];
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      const hoursStr = String(hours).padStart(2, "0");
      return `${hoursStr}:${minutes} ${ampm}`;
    }
  }
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : String(value || "N/A");
};

const parseTimeToMinutes = (val) => {
  if (!val) return null;
  if (typeof val === "string") {
    const match = val.match(/(\d{1,2}):(\d{2})(?:\s*(AM|PM))?/i);
    if (match) {
      let hours = parseInt(match[1], 10);
      const mins = parseInt(match[2], 10);
      const meridiem = match[3] ? match[3].toUpperCase() : null;
      if (meridiem === "PM" && hours < 12) hours += 12;
      if (meridiem === "AM" && hours === 12) hours = 0;
      return hours * 60 + mins;
    }
  }
  const d = new Date(val);
  if (!isNaN(d.getTime())) {
    return d.getHours() * 60 + d.getMinutes();
  }
  return null;
};

const calculateTripDurationMinutes = (startTime, endTime) => {
  const startMins = parseTimeToMinutes(startTime);
  const endMins = parseTimeToMinutes(endTime);
  if (startMins == null || endMins == null) return null;
  let timelineEnd = endMins;
  while (timelineEnd < startMins) {
    timelineEnd += 24 * 60;
  }
  return timelineEnd - startMins;
};

const SCHEDULE_TABLE_BATCH_SIZE = 10;

const readAvgOnTime = (schedule) => {
  const raw =
    schedule?.avgOnTime ??
    schedule?.averageOnTime ??
    schedule?.onTimePercent ??
    schedule?.onTimePercentage ??
    schedule?.avgOnTimePercent;
  if (raw == null || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? Math.max(0, Math.min(100, Math.round(value))) : null;
};

const EMPTY_ON_TIME_INDEX = {
  byId: {},
  byName: {},
  byRoute: {},
  byRouteId: {},
  routeVehicle: {},
};
const ON_TIME_LOOKBACK_DAYS = 30;
const ON_TIME_CHUNK_DAYS = 7;

const normalizeKey = (value) => String(value || "").trim().toLowerCase();

const scoreTripStatus = (status) => {
  const value = String(status || "").trim();
  const lower = value.toLowerCase();
  if (!value || lower.startsWith("in progress") || lower.includes("cancel")) return null;
  if (
    lower.includes("not completed") ||
    lower.includes("delay") ||
    lower.includes("late") ||
    lower.includes("over")
  ) {
    return "delayed";
  }
  if (
    value.startsWith("In-Time") ||
    value.startsWith("Early") ||
    value.startsWith("Completed") ||
    lower === "on time" ||
    lower === "ontime"
  ) {
    return "onTime";
  }
  return null;
};

const addBucket = (map, key, kind) => {
  if (!key || !kind) return;
  const current = map.get(key) || { onTime: 0, delayed: 0 };
  current[kind] += 1;
  map.set(key, current);
};

const bucketPercent = (bucket) => {
  if (!bucket) return null;
  const scored = bucket.onTime + bucket.delayed;
  if (!scored) return null;
  return Math.round((bucket.onTime / scored) * 100);
};

const preferredVehicleKey = (name, number) => {
  const vehicleNumber = normalizeKey(number);
  const vehicleName = normalizeKey(name);
  if (vehicleNumber && vehicleNumber !== "n/a") return `num:${vehicleNumber}`;
  if (vehicleName && vehicleName !== "n/a") return `name:${vehicleName}`;
  return "";
};

const scheduleVehicleKeys = (schedule) => {
  const keys = (schedule?.vehicleSchedule || [])
    .map((item) =>
      preferredVehicleKey(
        item?.vehicle?.name,
        item?.vehicle?.VehicleNumber || item?.vehicle?.vehicleNumber,
      ),
    )
    .filter(Boolean);
  return [...new Set(keys)];
};

const percentFromBuckets = (buckets) => {
  const totals = buckets.reduce(
    (acc, bucket) => {
      if (!bucket) return acc;
      acc.onTime += bucket.onTime;
      acc.delayed += bucket.delayed;
      acc.found = true;
      return acc;
    },
    { onTime: 0, delayed: 0, found: false },
  );
  if (!totals.found) return null;
  return bucketPercent(totals);
};

const reportedOnTimeFor = (schedule, index) => {
  const bySchedule =
    index.byId[String(schedule.id)] ?? index.byName[normalizeKey(schedule.name)];
  if (bySchedule != null) return bySchedule;

  const routeId = schedule.route?.id != null ? String(schedule.route.id) : "";
  const routeName = normalizeKey(schedule.route?.name);
  const vehicleKeys = scheduleVehicleKeys(schedule);
  if (vehicleKeys.length) {
    return percentFromBuckets(
      vehicleKeys.map(
        (vehicleKey) =>
          (routeId && index.routeVehicle[`${routeId}::${vehicleKey}`]) ||
          (routeName && index.routeVehicle[`${routeName}::${vehicleKey}`]) ||
          null,
      ),
    );
  }

  if (routeId && index.byRouteId[routeId] != null) return index.byRouteId[routeId];
  if (routeName && index.byRoute[routeName] != null) return index.byRoute[routeName];
  return null;
};

const collectTripKeys = (row) => {
  const ids = new Set();
  const names = new Set();
  const addId = (value) => {
    if (value != null && value !== "") ids.add(String(value));
  };
  const addName = (value) => {
    const key = normalizeKey(value);
    if (key && key !== "n/a") names.add(key);
  };

  addId(row?.scheduleId ?? row?.scheduleID);
  addName(row?.scheduleName ?? row?.schedule?.name);
  addId(row?.schedule?.id);
  const schedules = Array.isArray(row?.schedules) ? row.schedules : [];
  schedules.forEach((item) => {
    if (item && typeof item === "object") {
      addId(item.scheduleId ?? item.id);
      addName(item.scheduleName ?? item.name);
    } else {
      addName(item);
    }
  });

  return {
    ids: [...ids],
    names: [...names],
    route: normalizeKey(row?.routeName ?? row?.route?.name ?? row?.route),
  };
};

const createBucketMaps = () => ({
  byId: new Map(),
  byName: new Map(),
  byRoute: new Map(),
  byRouteId: new Map(),
  routeVehicle: new Map(),
});

const accumulateTripRows = (maps, rows) => {
  rows.forEach((row) => {
    const kind = scoreTripStatus(row?.status);
    if (!kind) return;
    const keys = collectTripKeys(row);
    const routeId =
      row?.routeId != null && row.routeId !== "" && row.routeId !== "N/A"
        ? String(row.routeId)
        : "";
    const vehicleKey = preferredVehicleKey(row?.vehicleName, row?.vehicleNumber);
    keys.ids.forEach((id) => addBucket(maps.byId, id, kind));
    keys.names.forEach((name) => addBucket(maps.byName, name, kind));
    addBucket(maps.byRoute, keys.route, kind);
    addBucket(maps.byRouteId, routeId, kind);
    if (vehicleKey) {
      if (routeId) addBucket(maps.routeVehicle, `${routeId}::${vehicleKey}`, kind);
      if (keys.route) addBucket(maps.routeVehicle, `${keys.route}::${vehicleKey}`, kind);
    }
  });
};

const indexFromBuckets = (maps) => {
  const toPercentMap = (map) =>
    Object.fromEntries(
      [...map.entries()]
        .map(([key, bucket]) => [key, bucketPercent(bucket)])
        .filter(([, value]) => value != null),
    );

  return {
    byId: toPercentMap(maps.byId),
    byName: toPercentMap(maps.byName),
    byRoute: toPercentMap(maps.byRoute),
    byRouteId: toPercentMap(maps.byRouteId),
    routeVehicle: Object.fromEntries(maps.routeVehicle),
  };
};

const onTimeDateChunks = () => {
  const windowStart = new Date();
  windowStart.setDate(windowStart.getDate() - (ON_TIME_LOOKBACK_DAYS - 1));
  windowStart.setHours(0, 0, 0, 0);

  const chunks = [];
  let cursorEnd = new Date();
  cursorEnd.setHours(23, 59, 59, 999);

  while (cursorEnd >= windowStart) {
    const chunkStart = new Date(cursorEnd);
    chunkStart.setDate(chunkStart.getDate() - (ON_TIME_CHUNK_DAYS - 1));
    chunkStart.setHours(0, 0, 0, 0);
    if (chunkStart < windowStart) chunkStart.setTime(windowStart.getTime());
    chunks.push({ start: new Date(chunkStart), end: new Date(cursorEnd) });
    cursorEnd = new Date(chunkStart.getTime() - 1);
  }

  return chunks;
};

const fetchTripReportChunk = async (organizationId, start, end) => {
  const response = await axios.get(`${config.baseURL}/report/trip`, {
    params: {
      organizationId,
      dateOption: "custom",
      customStartDate: start.toISOString(),
      customEndDate: end.toISOString(),
      allVehicles: true,
      allRoutes: false,
      allDrivers: false,
      allStops: false,
      allSites: false,
    },
    withCredentials: true,
  });
  const payload = response.data || {};
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const loadOnTimeIndexInChunks = async (organizationId, onChunk) => {
  const maps = createBucketMaps();
  for (const chunk of onTimeDateChunks()) {
    const rows = await fetchTripReportChunk(organizationId, chunk.start, chunk.end);
    accumulateTripRows(maps, rows);
    onChunk(indexFromBuckets(maps));
  }
};

function AvgOnTimeBar({ value, loading }) {
  if (loading) {
    return (
      <span className="schedule-ontime grey" aria-busy="true">
        <CircularProgress size={16} thickness={5} />
      </span>
    );
  }
  const numeric =
    value == null || !Number.isFinite(Number(value))
      ? 0
      : Math.max(0, Math.min(100, Math.round(Number(value))));
  const tone =
    numeric === 0 ? "grey" : numeric >= 90 ? "green" : numeric >= 60 ? "orange" : "amber";
  return (
    <span className={`schedule-ontime ${tone}`}>
      <b>{numeric}%</b>
      <i>
        <em style={{ width: `${numeric}%` }} />
      </i>
    </span>
  );
}

export default function NewScheduleMain() {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermission();
  const { organizationInactive } = useUser();
  const userState = JSON.parse(localStorage.getItem("userState") || "{}");
  const organizationId = userState?.user?.organizationId;
  const [schedules, setSchedules] = useState([]);
  const [stopNames, setStopNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [routeFilter, setRouteFilter] = useState("");
  const [frequencyFilter, setFrequencyFilter] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showCreateSchedule, setShowCreateSchedule] = useState(false);
  const [expandedScheduleId, setExpandedScheduleId] = useState(null);
  const [selectedScheduleIds, setSelectedScheduleIds] = useState([]);
  const [onTimeIndex, setOnTimeIndex] = useState(EMPTY_ON_TIME_INDEX);
  const [onTimeLoading, setOnTimeLoading] = useState(true);

  const loadSchedules = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [scheduleResponse, stopResponse] = await Promise.all([
        axios.get(`${config.baseURL}/schedule/organization`, {
          params: { organizationId },
          withCredentials: true,
        }),
        axios
          .get(`${config.baseURL}/stop/organization`, { withCredentials: true })
          .catch(() => ({ data: [] })),
      ]);
      const data = scheduleResponse.data;
      const names = {};
      (Array.isArray(stopResponse.data) ? stopResponse.data : []).forEach(
        (stop) => {
          if (stop?.id != null && stop.name) names[stop.id] = stop.name;
        },
      );
      setStopNames(names);
      setSchedules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Unable to load schedules", error);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  useEffect(() => {
    if (!organizationId) {
      setOnTimeLoading(false);
      return undefined;
    }
    let cancelled = false;
    setOnTimeLoading(true);
    setOnTimeIndex(EMPTY_ON_TIME_INDEX);
    loadOnTimeIndexInChunks(organizationId, (index) => {
      if (cancelled) return;
      setOnTimeIndex(index);
      setOnTimeLoading(false);
    })
      .catch((error) => {
        console.error("Unable to load schedule on-time report", error);
      })
      .finally(() => {
        if (!cancelled) setOnTimeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const rows = useMemo(
    () => {
      return schedules.map((schedule) => {
        const reportedOnTime = reportedOnTimeFor(schedule, onTimeIndex);
        const stopList = Array.isArray(schedule.stopSchedules) && schedule.stopSchedules.length > 0
          ? schedule.stopSchedules
          : Array.isArray(schedule.stop_schedules)
          ? schedule.stop_schedules
          : [];
        const firstStop = stopList[0];
        const lastStop = stopList[stopList.length - 1];
        const boundaryStartTime = firstStop?.departureTime || firstStop?.arrivalTime || schedule.startTime || "";
        const boundaryEndTime = lastStop?.arrivalTime || lastStop?.departureTime || schedule.endTime || "";

        return {
          id: schedule.id,
          name: schedule.name || `Schedule-${schedule.id}`,
          description: schedule.description,
          route: schedule.route?.name || "N/A",
          vehicle:
            schedule.vehicleSchedule
              ?.map((item) => item.vehicle?.name)
              .filter(Boolean)
              .join(", ") || "N/A",
          vehicleNumber: schedule.vehicleSchedule
            ?.map(
              (item) =>
                item.vehicle?.VehicleNumber || item.vehicle?.vehicleNumber,
            )
            .filter(Boolean)
            .join(", "),
          frequency: frequencyLabel(schedule.repetition || schedule.repitation),
          startTime: formatTime(boundaryStartTime, localeFor(i18n.language)),
          endTime: formatTime(boundaryEndTime, localeFor(i18n.language)),
          boundaryStartTime,
          boundaryEndTime,
          rawStartTime: schedule.nextOccurrence?.start || boundaryStartTime,
          avgOnTime: readAvgOnTime(schedule) ?? reportedOnTime ?? 0,
          status: schedule.scheduleStatus || "SCHEDULED",
          original: schedule,
        };
      });
    },
    [schedules, i18n.language, onTimeIndex],
  );

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          (!routeFilter || row.route === routeFilter) &&
          (!frequencyFilter || row.frequency === frequencyFilter),
      ),
    [rows, routeFilter, frequencyFilter],
  );
  const metrics = useMemo(
    () => ({
      scheduled: rows.length,
      started: rows.filter((row) =>
        ["RUNNING", "COMPLETED"].includes(row.status),
      ).length,
      upcoming: rows.filter((row) => row.status === "SCHEDULED").length,
      delayed: rows.filter((row) => row.status === "DELAYED").length,
      completed: rows.filter((row) => row.status === "COMPLETED").length,
    }),
    [rows],
  );
  const routes = [
    "",
    ...new Set(rows.map((row) => row.route).filter((value) => value !== "N/A")),
  ];
  const frequencies = [
    "",
    ...new Set(
      rows.map((row) => row.frequency).filter((value) => value !== "N/A"),
    ),
  ];
  const displayCell = (value) =>
    !value || value === "N/A" ? t("N/A") : value;
  const headers = layout.scheduleColumns.map((column) => {
    if (column.key === "avgOnTime") {
      return {
        ...column,
        render: (row) => (
          <AvgOnTimeBar value={row.avgOnTime} loading={onTimeLoading} />
        ),
      };
    }
    if (column.key === "frequency") {
      return { ...column, render: (row) => t(row.frequency || "N/A") };
    }
    if (column.key === "vehicle") {
      return {
        ...column,
        render: (row) => (
          <span className="schedule-table-vehicle">
            <strong>{displayCell(row.vehicle)}</strong>
            {row.vehicleNumber ? <small>{row.vehicleNumber}</small> : null}
          </span>
        ),
      };
    }
    if (["route", "startTime", "endTime", "name"].includes(column.key)) {
      return { ...column, render: (row) => displayCell(row[column.key]) };
    }
    return column;
  });
  const canCreate =
    !organizationInactive && hasPermission("/api/schedule", "POST");
  const canUpdate =
    !organizationInactive && hasPermission("/api/schedule", "PUT");
  const canDelete =
    !organizationInactive && hasPermission("/api/schedule", "DELETE");
  const parsedUserState = JSON.parse(localStorage.getItem("userState") || "null");
  const currentOrgName = (parsedUserState?.organization?.name || "RouteEye").replace(/[^a-zA-Z0-9_-]/g, "_");
  const scheduleExportFileName = `${currentOrgName}_Schedules`;

  const handleDownloadSchedules = async () => {
    const orgId = parsedUserState?.user?.organizationId || getOrganizationId() || organizationId;
    const downloadFileName = `${scheduleExportFileName}.xlsx`;

    // 1. Get currently displayed schedules (filtered or selected)
    const targetData = (selectedScheduleIds && selectedScheduleIds.length > 0)
      ? filteredRows.filter((s) =>
          selectedScheduleIds.some((id) => String(id) === String(s.id))
        )
      : filteredRows;

    // 2. Map directly from the frontpage table data with all dropdown details
    const exportRows = targetData.map((row) => {
      const schedule = row.original || {};
      const stopList = Array.isArray(schedule.stopSchedules) && schedule.stopSchedules.length > 0
        ? schedule.stopSchedules
        : Array.isArray(schedule.stop_schedules)
        ? schedule.stop_schedules
        : Array.isArray(schedule.route?.stops)
        ? schedule.route.stops
        : [];

      let boundaryStartTime = row.boundaryStartTime || "";
      let boundaryEndTime = row.boundaryEndTime || "";
      if (!boundaryStartTime || !boundaryEndTime) {
        if (stopList.length > 0) {
          const firstStop = stopList[0];
          const lastStop = stopList[stopList.length - 1];
          boundaryStartTime = firstStop?.departureTime || firstStop?.arrivalTime || schedule.startTime || "";
          boundaryEndTime = lastStop?.arrivalTime || lastStop?.departureTime || schedule.endTime || "";
        } else {
          boundaryStartTime = schedule.startTime || "";
          boundaryEndTime = schedule.endTime || "";
        }
      }

      const repeatEveryVal = schedule.repeatEvery ?? 1;
      let repeatUnit = "day(s)";
      const repUpper = String(schedule.repitation || schedule.repetition || row.frequency || "").toUpperCase();
      if (repUpper.includes("WEEK")) repeatUnit = "week(s)";
      else if (repUpper.includes("MONTH")) repeatUnit = "month(s)";
      else if (repUpper.includes("YEAR")) repeatUnit = "year(s)";
      else if (repUpper.includes("HOUR")) repeatUnit = "hour(s)";
      const repeatEveryText = `${repeatEveryVal} ${repeatUnit}`;

      let endConditionText = "No End Date";
      if (schedule.endCondition === "BY_DATE" && schedule.endDate) {
        const d = new Date(schedule.endDate);
        if (!isNaN(d.getTime())) {
          const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
          const dd = String(d.getUTCDate()).padStart(2, "0");
          const yyyy = d.getUTCFullYear();
          endConditionText = `By ${mm}/${dd}/${yyyy}`;
        }
      } else if (schedule.endCondition === "AFTER_OCCURRENCES" && schedule.occurrences) {
        endConditionText = `After ${schedule.occurrences} occurrences`;
      }

      const durationMins = calculateTripDurationMinutes(boundaryStartTime, boundaryEndTime);
      const tripDurationText = durationMins != null ? `${durationMins} minutes` : "-";

      const stopsDetails = stopList.length > 0
        ? stopList
            .map((ss, idx) => {
              const stopName = ss.stop?.name || ss.stopName || ss.name || `Stop ${idx + 1}`;
              const arr = ss.arrivalTime ? `Arr: ${ss.arrivalTime}` : "";
              const dep = ss.departureTime ? `Dep: ${ss.departureTime}` : "";
              const times = [arr, dep].filter(Boolean).join(", ");
              return times ? `${stopName} (${times})` : stopName;
            })
            .join(" -> ")
        : "-";

      let startDateText = "-";
      if (schedule.startDate) {
        const d = new Date(schedule.startDate);
        if (!isNaN(d.getTime())) {
          const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
          const dd = String(d.getUTCDate()).padStart(2, "0");
          const yyyy = d.getUTCFullYear();
          startDateText = `${mm}/${dd}/${yyyy}`;
        }
      }

      const rawAvg = row.avgOnTime ?? schedule?.avgOnTime ?? schedule?.averageOnTime;
      const numericAvg = (rawAvg == null || !Number.isFinite(Number(rawAvg))) ? 0 : Math.max(0, Math.min(100, Math.round(Number(rawAvg))));
      const avgOnTimeDisplay = `${numericAvg}%`;

      const vehicleDisplayText = row.vehicle && row.vehicleNumber
        ? `${row.vehicle} (${row.vehicleNumber})`
        : (row.vehicle || row.vehicleNumber || "-");

      const startTimeDisplay = formatTime(boundaryStartTime, localeFor(i18n.language));
      const endTimeDisplay = formatTime(boundaryEndTime, localeFor(i18n.language));

      return {
        "Schedule": row.name || `Schedule-${row.id}`,
        "Route": row.route || "-",
        "Vehicle": vehicleDisplayText,
        "Frequency": row.frequency || "-",
        "Start Time": startTimeDisplay,
        "End Time": endTimeDisplay,
        "Avg. On Time%": avgOnTimeDisplay,
        // Inner dropdown details (collapsible under [+] button in Excel):
        "Vehicle Number": row.vehicleNumber || "-",
        "Repeat Every": repeatEveryText,
        "Start Date": startDateText,
        "End Date": endConditionText,
        "Trip Duration": tripDurationText,
        "Description": row.description || schedule.description || "-",
        "Trip Time Table": stopsDetails,
      };
    });

    try {
      // 3. Send exact frontpage rows via POST to backend Excel export
      await downloadBackendExcel({
        url: `${config.baseURL}/excel/download`,
        method: "POST",
        data: {
          table: "schedules",
          organizationId: orgId,
          format: "xlsx",
          includeDetails: true,
          rows: exportRows,
        },
        defaultFileName: downloadFileName,
      });
    } catch (err) {
      console.warn("Backend POST /excel/download schedules failed, trying table=schedule:", err);
      try {
        await downloadBackendExcel({
          url: `${config.baseURL}/excel/download`,
          method: "POST",
          data: {
            table: "schedule",
            organizationId: orgId,
            format: "xlsx",
            includeDetails: true,
            rows: exportRows,
          },
          defaultFileName: downloadFileName,
        });
      } catch (err2) {
        console.warn("Backend POST /excel/download schedule failed, trying GET fallback:", err2);
        try {
          await downloadBackendExcel({
            url: `${config.baseURL}/excel/download`,
            method: "GET",
            params: {
              organizationId: orgId,
              table: "schedule",
              format: "xlsx",
              includeDetails: true,
              ...(selectedScheduleIds && selectedScheduleIds.length > 0
                ? { scheduleIds: selectedScheduleIds.join(","), ids: selectedScheduleIds.join(",") }
                : {}),
            },
            defaultFileName: downloadFileName,
          });
        } catch (err3) {
          console.warn("Backend GET /excel/download table=schedule failed, trying /excel/download/schedule:", err3);
          try {
            await downloadBackendExcel({
              url: `${config.baseURL}/excel/download/schedule`,
              method: "GET",
              params: {
                organizationId: orgId,
                format: "xlsx",
                includeDetails: true,
              },
              defaultFileName: downloadFileName,
            });
          } catch (fallbackErr) {
            console.warn("Backend export not available yet, using client XLSX export fallback:", fallbackErr);
            const detailedHeaders = [
              { key: "Schedule", label: "Schedule" },
              { key: "Route", label: "Route" },
              { key: "Vehicle", label: "Vehicle" },
              { key: "Frequency", label: "Frequency" },
              { key: "Start Time", label: "Start Time" },
              { key: "End Time", label: "End Time" },
              { key: "Avg. On Time%", label: "Avg. On Time%" },
              { key: "Vehicle Number", label: "Vehicle Number" },
              { key: "Repeat Every", label: "Repeat Every" },
              { key: "Start Date", label: "Start Date" },
              { key: "End Date", label: "End Date" },
              { key: "Trip Duration", label: "Trip Duration" },
              { key: "Description", label: "Description" },
              { key: "Trip Time Table", label: "Trip Time Table" },
            ];
            exportTableToCSV(scheduleExportFileName, detailedHeaders, exportRows);
          }
        }
      }
    }
  };

  const tableHeaders = [
    ...headers,
    {
      key: "expand",
      label: "",
      width: "48px",
      align: "center",
      render: (row) => {
        const open = String(expandedScheduleId || "") === String(row.id || "");
        const Icon = open ? KeyboardArrowUp : KeyboardArrowDown;
        return (
          <span className="schedule-row-expand" aria-hidden="true">
            <Icon sx={{ fontSize: 18 }} />
          </span>
        );
      },
    },
  ];
  const toggleExpandedSchedule = (scheduleId) => {
    setShowCreateSchedule(false);
    setExpandedScheduleId((currentId) =>
      String(currentId || "") === String(scheduleId || "") ? null : scheduleId,
    );
  };

  const removeSchedule = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${config.baseURL}/schedule`, {
        data: { scheduleId: Number(deleteTarget.id) },
        withCredentials: true,
      });
      setSchedules((current) =>
        current.filter((item) => item.id !== deleteTarget.id),
      );
      setExpandedScheduleId(null);
    } catch (error) {
      console.error("Unable to delete schedule", error);
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <main className="new-schedule-page">
      <SubTitleNew
        title="Schedules"
        path="Vehicle > Schedules"
        add={canCreate ? "Create Schedule" : null}
        onAddClick={
          canCreate
            ? () => {
                setShowCreateSchedule((open) => !open);
                setExpandedScheduleId(null);
              }
            : null
        }
      />
      <div className="new-schedule-content">
        {canCreate && showCreateSchedule && (
          <div className="vehicle-new-create-panel">
            <AddSchedule
              embedded
              onCancel={() => setShowCreateSchedule(false)}
              onComplete={() => {
                setShowCreateSchedule(false);
                loadSchedules();
              }}
            />
          </div>
        )}
        <div className="schedule-card-grid">
          {layout.stats.map((card) => (
            <StatsCard
              key={card.id}
              title={card.label}
              value={metrics[card.metric]}
              tone={card.tone}
              icon={ICONS[card.icon]}
              loading={loading}
            />
          ))}
        </div>
        <DynamicTable
          title="Schedules"
          headers={tableHeaders}
          data={filteredRows}
          downloadData={filteredRows}
          downloadFileName={scheduleExportFileName}
          selectable={true}
          selectedRowKeys={selectedScheduleIds}
          onSelectionChange={setSelectedScheduleIds}
          onDownload={handleDownloadSchedules}
          loading={loading}
          searchPlaceholder="Search routes"
          pagination={{ mode: "showMore", pageSize: SCHEDULE_TABLE_BATCH_SIZE, itemLabel: "schedules" }}
          onRowClick={canUpdate ? (row) => toggleExpandedSchedule(row.id) : undefined}
          renderExpandedRow={
            canUpdate
              ? (row) => {
                  if (String(expandedScheduleId || "") !== String(row.id || "")) {
                    return null;
                  }
                  return (
                    <div className="vehicle-new-expanded-form">
                      <AddSchedule
                        embedded
                        embeddedScheduleId={row.id}
                        onCancel={() => setExpandedScheduleId(null)}
                        onComplete={() => {
                          setExpandedScheduleId(null);
                          loadSchedules();
                        }}
                        onDelete={canDelete ? () => setDeleteTarget(row) : undefined}
                      />
                    </div>
                  );
                }
              : undefined
          }
          filters={[
            {
              key: "route",
              label: "All Routes",
              value: routeFilter,
              onChange: setRouteFilter,
              options: routes.map((value) => ({
                value,
                label: value || "All Routes",
              })),
            },
            {
              key: "frequency",
              label: "All Frequency",
              value: frequencyFilter,
              onChange: setFrequencyFilter,
              options: frequencies.map((value) => ({
                value,
                label: value || "All Frequency",
              })),
            },
          ]}
        />
        <ScheduleDashboardSections
          sections={layout.sections}
          schedules={rows}
          tripColumns={layout.tripColumns}
          stopNames={stopNames}
        />
      </div>
      <DeleteModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={removeSchedule}
        title="Delete Schedule"
        message={`${t("Are you sure you want to delete")} ${deleteTarget?.name || t("this schedule")}?`}
      />
    </main>
  );
}
